import { Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import PropTypes from "prop-types";
import { useCallback } from "react";
import {
  AT_LEAST_ONE_NUMBER,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  PASSWORD_CASE_REGEX,
  PASSWORD_LENGTH_REGEX_15_50,
  PASSWORD_LENGTH_REGEX_8_50,
  PrimaryFormConfirmPassword,
  PrimaryFormCurrentPassword,
  PrimaryFormPassword,
} from "react-common-ui-library";
import { StyledButton } from "react-common-ui-library/FPACT/Buttons";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CONFIRM_PASSWORD_FIELD_NAME } from "../../../Constants/AccountSetupConstants";
import {
  BUTTON,
  CANCEL,
  CONTAINED,
  EMPTY_STRING,
  FORGOT_PASSWORD_ROUTE,
  LOGIN_INFORMATION_ROUTE,
  LOGIN_ROUTE,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  SPACING_3_4,
  TEXT
} from "../../../Constants/CommonConstants";
import {
  NEW_PASSWORD,
  NEW_PASSWORD_LABEL,
  RESET_PASSWORD_BUTTON,
  RESET_PASSWORD_SUBMIT_BUTTON,
} from "../../../Constants/ResetPasswordConstants";
import { manageUtilityDataSelector } from "../../../Selectors/UtilityDataSelector";
import {
  AccountStyledButtonWrapper,
  StyledActionButtonsBox,
  StyledFormInputsBox,
} from "../../../Styles/Common/CommonStyles";
import { StyledPasswordFormInputsBox } from "../../../Styles/Login/ForgotPassword/ResetPasswordStyles";
import { validateNewPasswordResponse } from "./ResetPasswordUtils";

/**
 * @description ResetPassword component contains New Password and Confirm Password to reset the password.
 * @param {Function} function prop to handle the button click form submission
 * @returns {ReactNode} Render the New Password and Confirm Password inputs with action buttons.
 */
const ResetPasswordFormElements = ({ handleOnSubmit }) => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const location = useLocation();

  const methods = useFormContext();

  const confirmPasswordFieldValue = useWatch({
    name: CONFIRM_PASSWORD_FIELD_NAME,
  });

  const { isLoading } = useSelector((state) =>
    manageUtilityDataSelector(state)
  );

  const { previousScreen, isConsumer } = location.state;

  const navigateToLoginPage = () => {
    navigate(`/${LOGIN_ROUTE}`);
  };
  const navigateToLoginInformationPage = () => {
    navigate(
      `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${LOGIN_INFORMATION_ROUTE}`
    );
  };

  const passwordAsyncValidation = useCallback(
    async (value) => {
      if (
        (!isConsumer
          ? PASSWORD_LENGTH_REGEX_15_50.test(value)
          : PASSWORD_LENGTH_REGEX_8_50.test(value)) &&
        PASSWORD_CASE_REGEX.test(value) &&
        (AT_LEAST_ONE_NUMBER.test(value) ||
          AT_LEAST_ONE_SPECIAL_CHARACTER.test(value))
      ) {
        const { errorResponse, successResponse } =
          await validateNewPasswordResponse(value, dispatch, previousScreen);
        return { errorResponse, successResponse };
      }
      return { errorResponse: EMPTY_STRING, successResponse: [] };
    },
    [dispatch, isConsumer, previousScreen]
  );

  const handleCancel = () => {
    switch (previousScreen) {
      case LOGIN_INFORMATION_ROUTE:
        navigateToLoginInformationPage();
        break;
      case LOGIN_ROUTE:
      case FORGOT_PASSWORD_ROUTE:
        navigateToLoginPage();
        break;
      default:
        break;
    }
  };

  return (
    <Stack spacing={7}>
      <Stack spacing={4}>
        <StyledFormInputsBox>
          <StyledPasswordFormInputsBox>
            {previousScreen === LOGIN_INFORMATION_ROUTE ? (
              <PrimaryFormCurrentPassword />
            ) : null}
            <PrimaryFormPassword
              fieldProps={{
                passwordAsyncValidation: passwordAsyncValidation,
                previous24PasswordsCheck: true,
                isAdminOrProvider: !isConsumer,
                disabled: isLoading,
                deps: [
                  !isEmpty(confirmPasswordFieldValue)
                    ? CONFIRM_PASSWORD_FIELD_NAME
                    : EMPTY_STRING,
                ],
              }}
              labelProps={{ labelText: NEW_PASSWORD_LABEL }}
              formProps={{ name: NEW_PASSWORD }}
            />
            <PrimaryFormConfirmPassword />
          </StyledPasswordFormInputsBox>
        </StyledFormInputsBox>

        <AccountStyledButtonWrapper>
          <StyledActionButtonsBox>
            <Stack spacing={SPACING_3_4}>
              <StyledButton
                variant={CONTAINED}
                fullWidth
                id={RESET_PASSWORD_SUBMIT_BUTTON}
                type={BUTTON}
                onClick={methods?.handleSubmit(handleOnSubmit)}
              >
                {intl.formatMessage({
                  id: RESET_PASSWORD_BUTTON,
                })}
              </StyledButton>
              <StyledButton
                variant={TEXT}
                fullWidth
                id={CANCEL}
                onClick={handleCancel}
              >
                {intl.formatMessage({
                  id: CANCEL,
                })}
              </StyledButton>
            </Stack>
          </StyledActionButtonsBox>
        </AccountStyledButtonWrapper>
      </Stack>
    </Stack>
  );
};

export default ResetPasswordFormElements;

ResetPasswordFormElements.propTypes = {
  handleOnSubmit: PropTypes.func.isRequired,
};
